import { createContext, useState } from 'react'
import type { ReactNode } from 'react'
import { getVtexDocument, getVtexOrder, getVtexOrderConversation, getVtextInteractions, searchVtexOrders } from '@/services/vtex'
import type { VtexOrder, VtexConversationMessage, VtexOrderListItem, VtexInteraction } from '@/interfaces'
import type { DocumentMasterData } from '@/interfaces/masterData'

export const ORDER_STORAGE_KEY = 'conciex:order'
export const CONVERSATION_STORAGE_KEY = 'conciex:conversation'
export const INTERACTIONS_STORAGE_KEY = 'conciex:interactions'
export const SELECTED_ORDER_ID_KEY = 'conciex:selectedOrderId'
export const TICKET_ID_KEY = 'conciex:ticket-id'
export const JADLOG_CARRIER_KEY = 'conciex:is-jadlog-carrier'
export const TOTAL_EXPRESS_CARRIER_KEY = 'conciex:is-total-express-carrier'
export const PATRUS_CARRIER_KEY = 'conciex:is-patrus-carrier'
export const PATRUS_TOKEN_KEY = 'conciex:patrus-token'

interface OrderContextValue {
  order: VtexOrder | null
  conversation: VtexConversationMessage[]
  orderList: VtexOrderListItem[]
  isLoading: boolean
  isLoadingList: boolean
  error: string | null
  documents: DocumentMasterData[]
  fetchOrder: (orderId: string) => Promise<void>
  fetchDocuments: (documentId: string) => Promise<void>
  fetchOrderList: (q: string, incompleteOrders?: boolean) => Promise<void>
  setOrder: (order: VtexOrder | null) => void
  setConversation: (conversation: VtexConversationMessage[]) => void
  interactions: VtexInteraction[]
  setInteractions: (interactions: VtexInteraction[]) => void
  setDocuments: (documents: DocumentMasterData[]) => void
  isJadlogCarrier: boolean
  setIsJadlogCarrier: (value: boolean) => void
  isTotalExpressCarrier: boolean
  setIsTotalExpressCarrier: (value: boolean) => void
  isPatrusCarrier: boolean
  setIsPatrusCarrier: (value: boolean) => void
}

// eslint-disable-next-line react-refresh/only-export-components
export const OrderContext = createContext<OrderContextValue | null>(null)

export function OrderProvider({ children }: { children: ReactNode }) {
  const [order, setOrder] = useState<VtexOrder | null>(() => {
    try {
      const stored = localStorage.getItem(ORDER_STORAGE_KEY)
      return stored ? (JSON.parse(stored) as VtexOrder) : null
    } catch {
      return null
    }
  })
  const [conversation, setConversation] = useState<VtexConversationMessage[]>(() => {
    try {
      const stored = localStorage.getItem(CONVERSATION_STORAGE_KEY)
      return stored ? (JSON.parse(stored) as VtexConversationMessage[]) : []
    } catch {
      return []
    }
  })
  const [interactions, setInteractions] = useState(() => {
    try {
      const stored = localStorage.getItem(INTERACTIONS_STORAGE_KEY)
      return stored ? (JSON.parse(stored) as VtexInteraction[]) : []
    } catch {
      return []
    }
  })
  const [documents, setDocuments] = useState<DocumentMasterData[]>([])
  const [orderList, setOrderList] = useState<VtexOrderListItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingList, setIsLoadingList] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isJadlogCarrier, setIsJadlogCarrier] = useState<boolean>(() => {
    return localStorage.getItem(JADLOG_CARRIER_KEY) === 'true'
  })
  const [isTotalExpressCarrier, setIsTotalExpressCarrier] = useState<boolean>(() => {
    return localStorage.getItem(TOTAL_EXPRESS_CARRIER_KEY) === 'true'
  })
  const [isPatrusCarrier, setIsPatrusCarrier] = useState<boolean>(() => {
    return localStorage.getItem(PATRUS_CARRIER_KEY) === 'true'
  })
  async function fetchOrderList(q: string, incompleteOrders = false) {
    setIsLoadingList(true)
    setError(null)
    try {
      const list = await searchVtexOrders(q, incompleteOrders)
      setOrderList(list)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao buscar pedidos')
    } finally {
      setIsLoadingList(false)
    }
  }

  async function fetchOrder(orderId: string) {
    setIsLoading(true)
    setError(null)
    try {
      const [data, messages, interactionsData] = await Promise.all([
        getVtexOrder(orderId),
        getVtexOrderConversation(orderId),
        getVtextInteractions(orderId),
      ])
      localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(data))
      localStorage.setItem(CONVERSATION_STORAGE_KEY, JSON.stringify(messages))
      localStorage.setItem(INTERACTIONS_STORAGE_KEY, JSON.stringify(interactionsData))
      setOrder(data)
      setConversation(messages)
      setInteractions(interactionsData)

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao buscar pedido')
    } finally {
      setIsLoading(false)
    }
  }

  async function fetchDocuments(documentId: string) {
    setIsLoading(true)
    try {
      const documentsData = await getVtexDocument(documentId)
      setDocuments(documentsData)
    } catch (err) {
      console.warn('Erro ao buscar documentos (continuando sem documentos):', err)
      setDocuments([])
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <OrderContext.Provider value={{ order, conversation, orderList, isLoading, isLoadingList, error, fetchOrder, fetchDocuments, fetchOrderList, setOrder, setConversation, interactions, setInteractions, documents, setDocuments, isJadlogCarrier, setIsJadlogCarrier, isTotalExpressCarrier, setIsTotalExpressCarrier, isPatrusCarrier, setIsPatrusCarrier }}>
      {children}
    </OrderContext.Provider>
  )
}
